import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { BaseService } from 'src/app/core/base/base.service';
import {
  CustomerGroupComptableLine,
  CustomerGroupModel,
  CustomerGroupValidator,
} from '../../../../core/models/customer-group.model';
import { map, tap } from 'rxjs/operators';
import { ObjectApiInterface } from '../../../../core/base/interfaces/object-api.interface';
import {
  UserProfileValidatorLevel,
  UserProfileValidatorSeuil,
} from '../../../../core/enums/user-profile-validator-seuil.enum';
import { UserProfileValidator } from '../../../../core/models/user-profile.model';
import { CategoryModel } from '../../../../core/models/category.model';
import { AuthService } from '../../../../core/services/auth.service';
import { BehaviorSubject, Observable, of } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class CustomerGroupService extends BaseService<CustomerGroupModel> {
  defaultSort = 'name';
  private _currentCustomerGroup = new BehaviorSubject<ObjectApiInterface<CustomerGroupModel>>(null);
  readonly currentCustomerGroup$ = this._currentCustomerGroup.asObservable();

  constructor(
    public http: HttpClient,
    public authService: AuthService,
  ) {
    super(http, 'customer-group', authService);
  }

  setValidatorsChecked(checked: boolean) {
    this._entity.value.checkValidator = checked;
  }

  getCurrent(): Observable<ObjectApiInterface<CustomerGroupModel>> {
    if (this._currentCustomerGroup.value) {
      this._entity.next(this._currentCustomerGroup.value.data);
      return of(this._currentCustomerGroup.value);
    }

    return this.http.get<ObjectApiInterface<CustomerGroupModel>>(`${this.baseUrlApi}/getCurrent`).pipe(
      tap(x => {
        this._entity.next(x.data);
        this._currentCustomerGroup.next(x);
      }),
    );
  }

  refreshCurrent(): Observable<ObjectApiInterface<CustomerGroupModel>> {
    return this.http.get<ObjectApiInterface<CustomerGroupModel>>(`${this.baseUrlApi}/getCurrent`).pipe(
      tap(x => {
        this._entity.next(x.data);
        this._currentCustomerGroup.next(x);
      }),
    );
  }

  getValidators(matriceLevel?: UserProfileValidatorLevel) {
    return this.http.get<ObjectApiInterface<any>>(`${this.baseUrlApi}/getValidators?matriceLevel=${matriceLevel}`).pipe(
      map(x => {
        if (this._entity.value?.validators) {
          this._entity.value.validators = x.data;
        }
        return x;
      }),
    );
  }

  setValidator(category: CategoryModel) {
    return this.http.post<ObjectApiInterface<any>>(`${this.baseUrlApi}/setValidator`, category).pipe(
      map(x => {
        const exist = this._entity.value?.validators?.find(y => y.category === x.data.validators.category);

        x.data.validators.children = x.data.validators?.children?.map(z => {
          if (z?._id) {
            z.category = z._id;
          }
          return z;
        });

        if (exist) {
          this._entity.value.validators.map(y => {
            if (y.category === x.data.validators.category) {
              y.seuilA = x.data.validators.seuilA;
              y.seuilB = x.data.validators.seuilB;
              y.seuilC = x.data.validators.seuilC;
              y.standardCommand = x.data.validators.standardCommand;
              y.urgentCommand = x.data.validators.urgentCommand;
              y.children = x.data.validators.children;
            }
            return y;
          });
        } else {
          this._entity.value.validators?.push(x.data.validators);
        }

        this._entity.value.checkValidator = x.data.checkValidator;
        return x;
      }),
    );
  }

  updateComptableDetails(data) {
    return this.http.put<ObjectApiInterface<any>>(`${this.baseUrlApi}/comptable/update`, data).pipe(
      map(x => {
        this._entity.next(x.data);
        return x;
      }),
    );
  }

  findAllAccountantLines(
    page?: number,
    perPage?: number,
    sortField: string = 'accountNumber',
    sortOrder: string = 'asc',
    search?: string,
    filters?: any,
  ) {
    // BUG TVA filters is null
    if (filters && !filters.isArchive) {
      filters.isArchive = false;
    }
    return this.http.get<ObjectApiInterface<any[]>>(`${this.baseUrlApi}/comptable/line/list`, {
      params: new HttpParams()
        .set('page', page?.toString() ?? '1')
        .set('perPage', perPage?.toString() ?? '100')
        .set('sort', `${sortField}@${sortOrder}`)
        .set('search', search ?? '')
        .set('filters', JSON.stringify(filters) ?? ''),
    });
  }

  findFilteredComptableLines(state: string = '', data: any) {
    // BUG TVA filters is null
    let code: any;
    if (data?.genericProduct?.genericAccountingAccount && !data?.genericPrestation?.genericAccountingAccount)
      code = null;
    if (data?.genericProduct?.genericAccountingAccount) {
      code = data?.genericProduct?.genericAccountingAccount?.code;
    } else if (data?.genericPrestation?.genericAccountingAccount) {
      code = data?.genericPrestation?.genericAccountingAccount?.code;
    } else {
      code = null;
    }
    if (state !== '') {
      code = null;
    }

    return this.findAllAccountantLines(null, null, 'accountNumber', 'asc', state, { accountNumber: code });
  }

  createComptableLine(data) {
    return this.http.post<any>(`${this.baseUrlApi}/comptable/line/create`, data).pipe(
      map(x => {
        this._entity.value.comptable.lines.push(x);
        return x;
      }),
    );
  }

  updateComptableLine(data) {
    return this.http.put<ObjectApiInterface<any>>(`${this.baseUrlApi}/comptable/line/update`, data).pipe(
      map(x => {
        this._entity.next(x.data);
        return x;
      }),
    );
  }

  importComptable(entity) {
    return this.http.post(`${this.baseUrlApi}/comptable/line/import`, entity);
  }

  downloadExcelComptableTemplate() {
    return this.http.get(`${this.baseUrlApi}/comptable/line/download-excel-template`);
  }

  getGedUrl(): Observable<any> {
    return this.http.get(`${this.baseUrlApi}/getGedUrl`);
  }

  setGedUrl(gedUrls: any): Observable<any> {
    return this.http.put<ObjectApiInterface<any>>(`${this.baseUrlApi}/setGedUrl`, { gedUrls }).pipe(
      map(x => {
        this._entity.next(x.data);
        return x;
      }),
    );
  }

  validateStatus(): Observable<any> {
    return this.http.get<ObjectApiInterface<any>>(`${this.baseUrlApi}/validateStatus`).pipe(
      map(x => {
        this._entity.next(x.data);
        return x;
      }),
    );
  }
}
